import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CalculatorForm from "../components/calculator/CalculatorForm";
import CalculatorResult from "../components/calculator/CalculatorResult";

const DEFAULT_FORM = {
  base_stake: 10,
  bankroll: 1000,
  variant: "classic",
  win_probability: 0.5,
  american_odds: -110,
  table_limit: 2000,
  max_steps: 10,
};

export default function Calculator() {
  const { user } = useAuth();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        base_stake: user.base_stake || f.base_stake,
        bankroll: user.starting_bankroll || f.bankroll,
        table_limit: user.table_limit || f.table_limit,
      }));
    }
  }, [user]);

  const calc = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/martingale/calculate", {
        ...payload,
        base_stake: Number(payload.base_stake),
        bankroll: Number(payload.bankroll),
        win_probability: Number(payload.win_probability),
        american_odds: Number(payload.american_odds),
        table_limit: Number(payload.table_limit) || null,
        max_steps: Number(payload.max_steps),
      });
      setResult(data);
    } finally {
      setLoading(false);
    }
  }, []);

  // Compute once on mount using initial/default form; subsequent runs are user-driven.
  useEffect(() => {
    calc(DEFAULT_FORM);
  }, [calc]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="calculator-page">
      <div className="border-b border-zinc-800 pb-10 mb-10">
        <div className="overline mb-3">MATH·LAB</div>
        <h1 className="font-display text-4xl lg:text-5xl">The progression calculator.</h1>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          Plug in your bankroll, base stake, table limit and odds. We compute the exact
          sequence, your real maximum safe steps, and the probability of busting.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <CalculatorForm
          form={form}
          update={update}
          loading={loading}
          onSubmit={(e) => {
            e.preventDefault();
            calc(form);
          }}
        />
        <CalculatorResult result={result} />
      </div>
    </div>
  );
}
